import React, { useState, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import Cropper from 'react-easy-crop';
import { Box, Divider, Slider, Typography } from '@mui/material';
import { getOrientation } from 'get-orientation/browser';
import CustomDialog from '../../sharedcomponents/dialog/dialog';
import ButtonContainer from '../../sharedcomponents/buttoncontainer/buttonContainer';
import ImgDialog from './imgDialog';
import { getCroppedImg, getRotatedImage } from './canvasUtils';
import { v1 as uuidv1 } from 'uuid';
import { createTheme } from '@mui/material/styles';
import demoImg from '../../assets/images/demoImg.jpg';

const theme = createTheme();

const useStyles = makeStyles(() => ({
    title: {
        color: '#0c2c75',
        fontSize: '18px',
        padding: '10px 20px',
        borderBottom: '1px solid',
    },
    selectparticipant: {
        padding: theme.spacing(1.5),
        borderTop: '1px solid #0c2c75',
    },
    setScale: {
        transform: 'scale(1.5)',
    },
    dialogHeight: {
        // minHeight: "275px",
        // maxHeight: "275px"
    },
    cropContainer: {
        position: 'relative',
        width: '100%',
        height: 200,
        background: '#333',
        [theme.breakpoints.up('sm')]: {
            height: 250,
        },
    },
    cropButton: {
        flexShrink: 0,
        marginLeft: 16,
    },
    controls: {
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'Left',
        },
    },
    sliderContainer: {
        display: 'flex',
        flex: '1',
        alignItems: 'Left',
    },
    sliderLabel: {
        [theme.breakpoints.down('xs')]: {
            //  minWidth: 65,
        },
    },
    slider: {
        padding: '22px 0px',
        marginLeft: '16px',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'center',
            margin: '0 16px',
        },
    },
    fileInput: {
        display: 'flex',
        flex: '1',
        alignItems: 'center',
    },
    inputIcon: {
        justifyContent: 'center',
        alignItems: 'center', 
        marginTop:'10%',
        height: 250,
        width: 275,
    },
}));



const ORIENTATION_TO_ANGLE = {
    '3': 180,
    '6': 90,
    '8': -90,
};


const UploadImagePopup = (props) => {
    const classes = useStyles();
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [fileType, setFileType] = useState('');
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [fileName, setFileName] = useState('');
    const [disableCancel, setDisableCancel] = useState(false);
    const [snackBarProps, setSnackBarProps] = React.useState({
        openSnack: false,
        variant: 'error',
        message: '',
    });

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const showCroppedImage = useCallback(async () => {
        try {
            setDisableCancel(true);
            const croppedImage = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                rotation,
                fileType,
                fileName
            );
            setImageSrc(null);
            props.changeProfilePhoto(croppedImage);
        } catch (e) {
            console.error(e);
        }
    }, [imageSrc, croppedAreaPixels, rotation]);

    const onClose = useCallback(() => {
        setCroppedImage(null);
    }, []);

    const onFileChange = async e => {

        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];


            if (file.type == "image/jpeg" ||
                file.type == "image/jpg" || file.type == "image/png" || file.type == "image/gif") {


                // props.setViewFile(file);
                setFileType(file.type);
                let blob = file.slice(0, file.size, file.type);
                let extension = file.name.substring(file.name.lastIndexOf('.') + 1);
                let newFileName = uuidv1() + '.' + extension;
                setFileName(newFileName);
                const newFile = new File([blob], newFileName, { type: file.type });

                let imageDataUrl = await readFile(newFile);
                // apply rotation if needed
                const orientation = await getOrientation(newFile);
                const rotation = ORIENTATION_TO_ANGLE[orientation];
                if (rotation) {
                    imageDataUrl = await getRotatedImage(imageDataUrl, rotation, file.type);
                }
                // console.log(file.type);


                setImageSrc(imageDataUrl);
            } else {
                setImageSrc(null);
                e.target.value = null;
                props.setSnackBarProps({
                    ...snackBarProps,
                    openSnack: true,
                    variant: 'error',
                    message: 'Please upload only jpg,png,gif',
                });
            }

        }
    };

    const handleCancel = () => {
        if (imageSrc != null)
            setImageSrc(null);
        else
            props.handleClose();
    };

    const renderDialogContent = () => (<>
        {imageSrc ? (
            <>
                <div className={classes.cropContainer}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        rotation={rotation}
                        zoom={zoom}
                        aspect={4 / 4}
                        onCropChange={setCrop}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        // cropShape={props.showExtraDescription ? "" : 'round'}
                    />
                </div>

                <div className={classes.controls}>
                    <div className={classes.sliderContainer}>
                        <Typography
                            variant="overline"
                            classes={{ root: classes.sliderLabel }}
                            style={{ minWidth: '50px' }}
                        >
                            Zoom
                        </Typography>

                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            classes={{ container: classes.slider }}
                            onChange={(e, zoom) => setZoom(zoom)}
                        />
                    </div>
                    <br />
                    <div className={classes.sliderContainer}>
                        <Typography
                            variant="overline"
                            classes={{ root: classes.sliderLabel }}
                            style={{ minWidth: '75px' }}
                        >
                            Rotation
                        </Typography>
                        <Slider
                            value={rotation}
                            min={0}
                            max={360}
                            step={1}
                            aria-labelledby="Rotation"
                            classes={{ container: classes.slider }}
                            onChange={(e, rotation) => setRotation(rotation)}
                        />
                    </div>
                </div>
                <ImgDialog img={croppedImage} onClose={onClose} />
            </>
        ) : (
            <>
            <Box>
                <input type="file" style={{fontSize:props.showExtraDescription ? '16px' : '', marginBottom:"24px"}} onChange={onFileChange} className={classes.fileInput} accept=".png, .jpg, .jpeg,.gif " />
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                textAlign: 'left',
                justifyContent: 'left',
                alignItems: 'left',
                flexbasis: '1080px'
            }}> 
                <Box sx={{
                    width: '480px',
                    flexBasis: '480px',
                    flexGrow: 1,
                }}>
                    
                        {props.showExtraDescription && <> <Box>
                            <Typography
                                variant="h4"
                                fontSize={{ xs: '12px', md: '12px' }}
                                fontWeight={500}
                                paddingTop={1}
                                color="primary">
                                Instructions
                            </Typography>
                        </Box>
                            <Divider
                                sx={{ mt: 1, mb: 1 }}
                                flexItem
                            /></>}
                    <Box>
                        <Typography
                            variant="div"
                            fontSize={{ xs: '12px', md: '12px' }}
                            fontWeight={500}
                            paddingTop={0}>
                            -  File format: Jpg, Png, gif
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            variant="p"
                            fontSize={{ xs: '12px', md: '12px' }}
                            fontWeight={500}
                            paddingTop={0}>
                            -	File must in a .jpeg, .jpg or .png format
                        </Typography>
                    </Box>
                    {props.showExtraDescription &&
                        <> <Box>
                            <Typography
                                variant="p"
                                fontSize={{ xs: '12px', md: '12px' }}
                                fontWeight={500}
                                paddingTop={0}>
                                -	Photos must be clear, sharp and in focus
                            </Typography>
                        </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	Photos must show both edges of the face clearly
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	Photos must show a full front view of face and shoulders
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	The face and shoulder image must be centered
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	There must be no hair across the eyes
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	No Hats or head coverings except for religious reasons
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	Full facial features are clearly visible
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	Photo has no shadows
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	Photo has uniform lighting
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	No flash reflection on the face
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="p"
                                    fontSize={{ xs: '12px', md: '12px' }}
                                    fontWeight={500}
                                    paddingTop={0}>
                                    -	Photo has a plain, uniform, light background
                                </Typography>
                            </Box>
                        </>}
                </Box>
                {props.showExtraDescription && <Box sx={{
                    width: '480px',
                    flexBasis: '480px',
                    flexGrow: 1,
                }}>
                    <Box
                        component="img"
                        alt="pic." 
                        src={demoImg}
                        className={classes.inputIcon}
                    />
                </Box>}
            </Box>
            </>
        )}
    </>
    );

    const renderDialogActions = () => (
        <ButtonContainer
            handleClick={showCroppedImage}
            handleCancel={handleCancel}
            buttonText={'Upload'}
            disableButton={imageSrc == null}
            disableCancel={disableCancel}
        />
    );

    return (
        <>
            <CustomDialog
                title={props.title}
                handleClose={props.handleClose}
                open={props.open}
                fullWidth={true}
                // maxWidth={'sm'}
                // title={'Export Itinerary'}
                renderContent={renderDialogContent}
                renderActions={renderDialogActions}
                setSnackBarProps={setSnackBarProps}

            />

        </>
    );
};

function readFile(file) {
    return new Promise(resolve => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result), false);
        reader.readAsDataURL(file);
    });
}


export default UploadImagePopup;